import type { CollectibleTier } from '../levels/types';
import { clamp } from '../util/math';

// Scoring (§4):  FinalScore = CollectibleValue*Combo + TimeBonus + AirBonus

export const TIER_VALUE: Record<CollectibleTier, number> = {
  fruit: 50,
  food: 150,
  coin: 400,
  envelope: 1500,
};

// [DECIDED, tunable] §4 / §12
const COMBO_WINDOW = 2.6; // seconds to chain the next pickup
const COMBO_STEP = 0.25; // multiplier gained per chained pickup
const COMBO_MAX = 8;
const TIME_RATE = 120; // points per second under par
const AIR_RATE = 40; // points per second airborne (light style bonus, §4)

export interface CollectResult {
  tier: CollectibleTier;
  baseValue: number;
  multiplier: number;
  gained: number;
  streak: number;
}

export interface ScoreBreakdown {
  collectibles: number;
  combo: number; // points attributable to the combo multiplier
  time: number;
  air: number;
  total: number;
  bestStreak: number;
}

export class Scoring {
  private collectibleBase = 0; // sum of base values (combo = 1)
  private collectibleScored = 0; // sum of value * multiplier
  private airScore = 0;
  private timeBonus = 0;

  streak = 0;
  bestStreak = 0;
  comboTimer = 0;

  reset(): void {
    this.collectibleBase = 0;
    this.collectibleScored = 0;
    this.airScore = 0;
    this.timeBonus = 0;
    this.streak = 0;
    this.bestStreak = 0;
    this.comboTimer = 0;
  }

  multiplierFor(streak: number): number {
    if (streak <= 1) return 1;
    return clamp(1 + COMBO_STEP * (streak - 1), 1, COMBO_MAX);
  }

  get currentMultiplier(): number {
    return this.streak > 0 ? this.multiplierFor(this.streak) : 1;
  }

  collect(tier: CollectibleTier): CollectResult {
    this.streak += 1;
    this.bestStreak = Math.max(this.bestStreak, this.streak);
    this.comboTimer = COMBO_WINDOW;
    const baseValue = TIER_VALUE[tier];
    const multiplier = this.multiplierFor(this.streak);
    const gained = Math.round(baseValue * multiplier);
    this.collectibleBase += baseValue;
    this.collectibleScored += gained;
    return { tier, baseValue, multiplier, gained, streak: this.streak };
  }

  /** Returns true if the combo just expired this frame. */
  update(dt: number, airborne: boolean): boolean {
    let expired = false;
    if (this.streak > 0) {
      this.comboTimer -= dt;
      if (this.comboTimer <= 0) {
        this.comboTimer = 0;
        this.streak = 0;
        expired = true;
      }
    }
    if (airborne) this.airScore += dt * AIR_RATE;
    return expired;
  }

  /** A fall breaks the combo (§5). */
  breakCombo(): void {
    this.streak = 0;
    this.comboTimer = 0;
  }

  /** Normalized combo timer for the HUD meter (0..1). */
  get comboFraction(): number {
    return clamp(this.comboTimer / COMBO_WINDOW, 0, 1);
  }

  get liveScore(): number {
    return Math.round(this.collectibleScored + this.airScore);
  }

  finalize(parTime: number, actualTime: number): ScoreBreakdown {
    this.timeBonus = Math.max(0, parTime - actualTime) * TIME_RATE;
    const collectibles = Math.round(this.collectibleBase);
    const combo = Math.round(this.collectibleScored - this.collectibleBase);
    const time = Math.round(this.timeBonus);
    const air = Math.round(this.airScore);
    return {
      collectibles,
      combo,
      time,
      air,
      total: collectibles + combo + time + air,
      bestStreak: this.bestStreak,
    };
  }
}
